import { apiGet, apiSend } from './client';

export type AdminBonusZoneServerLink = {
  id: string;
  serverId: number;
  serverSlug: string;
  serverName: string;
  isEnabled: boolean;
  sortOrder: number;
  enabledAt: string;
  disabledAt: string | null;
};

export type AdminBonusZoneExpansion = {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
};

export type AdminBonusZone = {
  id: string;
  slug: string;
  name: string;
  expansionId: string;
  expansion: string;
  expansionSlug: string;
  expansionInfo: AdminBonusZoneExpansion;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
  legacyZoneId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  expansionEnabledForServer: boolean;
  disabledByServerOverride: boolean;
  enabledForServer: boolean;
  serverOverride: { isEnabled: boolean; disabledAt: string | null } | null;
  serverSortOrder: number;
  serverZones: AdminBonusZoneServerLink[];
};

export type AdminBonusExpansion = AdminBonusZoneExpansion & {
  enabledForServer: boolean;
  serverExpansionId: string | null;
  unlocksAt: string | null;
  notes: string | null;
};

export type AdminBonusZoneListResponse = {
  server: { id: number; slug: string; name: string } | null;
  zones: AdminBonusZone[];
};

export type AdminBonusExpansionListResponse = {
  server: { id: number; slug: string; name: string };
  expansions: AdminBonusExpansion[];
};

export type AdminBonusZonePayload = {
  serverSlug?: string;
  name: string;
  slug?: string;
  expansionId: string;
  minLevel?: number | null;
  maxLevel?: number | null;
  zoneType?: string | null;
  legacyZoneId?: number | null;
  isActive?: boolean;
  isEnabled?: boolean;
};

export function fetchAdminBonusZones(serverSlug?: string) {
  const query = serverSlug ? `?serverSlug=${encodeURIComponent(serverSlug)}` : '';
  return apiGet<AdminBonusZoneListResponse>(`/admin/bonuses/zones${query}`);
}

export function createAdminBonusZone(payload: AdminBonusZonePayload) {
  return apiSend<{ zone: AdminBonusZone }>('/admin/bonuses/zones', 'POST', payload);
}

export function updateAdminBonusZone(zoneId: string, payload: AdminBonusZonePayload) {
  return apiSend<{ zone: AdminBonusZone }>(`/admin/bonuses/zones/${zoneId}`, 'PUT', payload);
}

export function setAdminBonusZoneEnabled(zoneId: string, serverSlug: string, isEnabled: boolean) {
  return apiSend<{ zone: AdminBonusZone }>(`/admin/bonuses/zones/${zoneId}/server-enabled`, 'POST', { serverSlug, isEnabled });
}

export function deactivateAdminBonusZone(zoneId: string) {
  return apiSend<{ zone: AdminBonusZone }>(`/admin/bonuses/zones/${zoneId}`, 'DELETE');
}

export function fetchAdminBonusExpansions(serverSlug: string) {
  return apiGet<AdminBonusExpansionListResponse>(`/admin/bonuses/expansions?serverSlug=${encodeURIComponent(serverSlug)}`);
}

export function setAdminBonusExpansionEnabled(expansionId: string, serverSlug: string, isEnabled: boolean) {
  return apiSend<AdminBonusExpansionListResponse>(`/admin/bonuses/expansions/${encodeURIComponent(expansionId)}/server-enabled`, 'POST', { serverSlug, isEnabled });
}
